import { z } from 'zod';
import type { TaxOfficeToken } from '@sleed/bizcommerce-api';

/**
 * Tax office (ΔΟΥ) edit form. Mirrors `TaxOfficeToken` and the legacy admin grid
 * (Core/client/admin/src/pages/System/TaxOffices/index.js): cd required ≤50, dsc ≤100,
 * adr ≤200, ct ≤50, pc ≤20, si required. cid (country) optional. tel/erpcd carried through.
 */
export const taxOfficeSchema = z.object({
	id: z.number().optional(),
	cd: z.string().trim().min(1, 'validation.required').max(50, 'validation.tooLong'),
	cid: z.number().nullable(),
	dsc: z.string().trim().max(100, 'validation.tooLong').nullable(),
	adr: z.string().trim().max(200, 'validation.tooLong').nullable(),
	ct: z.string().trim().max(50, 'validation.tooLong').nullable(),
	pc: z.string().trim().max(20, 'validation.tooLong').nullable(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	tel: z.string().nullable(),
	erpcd: z.string().nullable()
});

export type TaxOfficeFormValues = z.infer<typeof taxOfficeSchema>;

export const EMPTY_TAX_OFFICE: TaxOfficeFormValues = {
	cd: '',
	cid: null,
	dsc: null,
	adr: null,
	ct: null,
	pc: null,
	iv: true,
	si: 0,
	tel: null,
	erpcd: null
};

export function tokenToForm(token: TaxOfficeToken): TaxOfficeFormValues {
	return {
		id: token.id,
		cd: token.cd ?? '',
		cid: token.cid ?? null,
		dsc: token.dsc ?? null,
		adr: token.adr ?? null,
		ct: token.ct ?? null,
		pc: token.pc ?? null,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		tel: token.tel ?? null,
		erpcd: token.erpcd ?? null
	};
}

export function formToToken(form: TaxOfficeFormValues): TaxOfficeToken {
	return {
		id: form.id,
		cd: form.cd.trim(),
		cid: form.cid,
		dsc: form.dsc?.trim() || null,
		adr: form.adr?.trim() || null,
		ct: form.ct?.trim() || null,
		pc: form.pc?.trim() || null,
		iv: form.iv,
		si: form.si,
		tel: form.tel,
		erpcd: form.erpcd
	};
}
